// ─────────────────────────────────────────────────────────────────────────────
// LocationMapPicker — OpenStreetMap + Leaflet (zero API key required)
//
// Drop-in replacement for the previous Google Maps component.
// Props are 100% identical — HospitalRegistration.js needs no changes.
//
// Stack:
//   • Leaflet 1.9.4  — map rendering (CDN, injected at runtime)
//   • CartoDB Dark Matter tiles — matches dashboard dark theme
//   • Nominatim (OpenStreetMap) — free geocoding & reverse-geocoding for India
//
// Features (same as before):
//   • Click map to drop pin
//   • Draggable red hospital pin
//   • Search bar with live dropdown (Nominatim, India-biased)
//   • "Use my location" (browser Geolocation API)
//   • Reverse-geocode on every move → auto-fills city/state/district/pincode
//   • Confirmation card with lat/lng/address display
//   • onChange({ lat, lng, display, address, city, district, state, pincode })
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MdSearch, MdMyLocation, MdCheckCircle, MdLocationOn, MdEdit } from 'react-icons/md';

// ── CDN URLs ──────────────────────────────────────────────────────────────────
const LEAFLET_VERSION = '1.9.4';
const LEAFLET_CSS_URL = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const LEAFLET_JS_URL  = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;

// ── Geocoding: Google (accurate, India) when key set; else Nominatim fallback ─
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const NOMINATIM = 'https://nominatim.openstreetmap.org';
const NOM_EMAIL = 'hcenetworks@gmail.com';

// ── Map defaults ──────────────────────────────────────────────────────────────
const INDIA  = { lat: 20.5937, lng: 78.9629 };
const ZOOM_INDIA    = 5;
const ZOOM_LOCATION = 16;

// ── Leaflet loader (singleton — loads script + css once globally) ─────────────
let _leafletReady   = false;
let _leafletPromise = null;

function loadLeaflet() {
  if (_leafletReady && window.L) return Promise.resolve(window.L);
  if (_leafletPromise)           return _leafletPromise;

  _leafletPromise = new Promise((resolve, reject) => {
    // Inject Leaflet CSS once
    if (!document.querySelector(`link[data-leaflet-css]`)) {
      const link = document.createElement('link');
      link.rel  = 'stylesheet';
      link.href = LEAFLET_CSS_URL;
      link.setAttribute('data-leaflet-css', '1');
      document.head.appendChild(link);
    }

    // Inject light-theme overrides for Leaflet UI controls
    if (!document.querySelector('[data-leaflet-dark]')) {
      const style = document.createElement('style');
      style.setAttribute('data-leaflet-dark', '1');
      style.textContent = `
        .leaflet-control-zoom a {
          background: #ffffff !important;
          color: #333333 !important;
          border-color: #cccccc !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f0f0f0 !important;
          color: #000000 !important;
        }
        .leaflet-control-attribution {
          background: var(--text-primary) !important;
          color: #666666 !important;
          font-size: 9px !important;
        }
        .leaflet-control-attribution a { color: #3b82f6 !important; }
        .leaflet-bar { border: 1px solid #cccccc !important; border-radius: 8px !important; overflow: hidden; box-shadow: 0 2px 6px rgba(0,0,0,0.15) !important; }
      `;
      document.head.appendChild(style);
    }

    // If already injected by a previous component mount, wait for it
    if (window.L) { _leafletReady = true; resolve(window.L); return; }

    const existing = document.querySelector(`script[data-leaflet-js]`);
    if (existing) {
      existing.addEventListener('load',  () => { _leafletReady = true; resolve(window.L); });
      existing.addEventListener('error', () => reject(new Error('Leaflet script failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.src   = LEAFLET_JS_URL;
    script.async = true;
    script.setAttribute('data-leaflet-js', '1');
    script.onload  = () => { _leafletReady = true; resolve(window.L); };
    script.onerror = () => reject(new Error('Leaflet failed to load (check your internet connection)'));
    document.head.appendChild(script);
  });

  return _leafletPromise;
}

// ── Custom red hospital pin (SVG — same visual as before) ─────────────────────
function makeHospitalIcon(L) {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 46" width="36" height="46">' +
      '<path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 28 18 28S36 31.5 36 18C36 8.06 27.94 0 18 0z" fill="#ef4444"/>' +
      '<circle cx="18" cy="18" r="11" fill="#fff"/>' +
      '<rect x="16" y="11" width="4" height="14" rx="1" fill="#ef4444"/>' +
      '<rect x="11" y="16" width="14" height="4" rx="1" fill="#ef4444"/>' +
    '</svg>';
  return L.divIcon({
    className:   '',
    html:        svg,
    iconSize:    [36, 46],
    iconAnchor:  [18, 46],
    popupAnchor: [0, -46],
  });
}

function _pickGoogleComponent(comps, ...types) {
  for (const t of types) {
    const c = (comps || []).find(x => (x.types || []).includes(t));
    if (c) return c.long_name || c.short_name || '';
  }
  return '';
}

function _cleanIndianPlace(name) {
  return String(name || '')
    .replace(/ District$/i, '')
    .replace(/ Mandal$/i, '')
    .replace(/ Taluk$/i, '')
    .trim();
}

function _scoreGoogleResult(r, lat, lng) {
  const comps = r.address_components || [];
  let s = 0;
  if (comps.some(c => c.types.includes('postal_code'))) s += 12;
  if (comps.some(c => c.types.includes('administrative_area_level_2'))) s += 8;
  if (comps.some(c => c.types.includes('locality'))) s += 5;
  if (r.geometry?.location_type === 'ROOFTOP') s += 10;
  else if (r.geometry?.location_type === 'RANGE_INTERPOLATED') s += 6;
  else if (r.geometry?.location_type === 'GEOMETRIC_CENTER') s += 2;
  const glat = r.geometry?.location?.lat;
  const glng = r.geometry?.location?.lng;
  if (glat != null && glng != null) {
    s -= (Math.abs(glat - lat) + Math.abs(glng - lng)) * 500;
  }
  return s;
}

function _pickBestGoogleResult(results, lat, lng) {
  if (!results?.length) return null;
  return [...results].sort((a, b) => _scoreGoogleResult(b, lat, lng) - _scoreGoogleResult(a, lat, lng))[0];
}

function _buildAddrFromGoogle(result, lat, lng) {
  const comps = result.address_components || [];
  const city = _cleanIndianPlace(
    _pickGoogleComponent(comps, 'locality', 'administrative_area_level_3', 'sublocality_level_1', 'sublocality', 'neighborhood', 'postal_town')
  );
  let district = _cleanIndianPlace(
    _pickGoogleComponent(comps, 'administrative_area_level_2', 'administrative_area_level_3')
  );
  const state = _pickGoogleComponent(comps, 'administrative_area_level_1');
  let pincode = (_pickGoogleComponent(comps, 'postal_code') || '').replace(/\D/g, '').slice(0, 6);
  const premise = _pickGoogleComponent(comps, 'premise', 'establishment', 'point_of_interest', 'hospital');
  const route = _pickGoogleComponent(comps, 'route', 'street_address');
  const sublocality = _pickGoogleComponent(comps, 'sublocality_level_2', 'sublocality_level_1', 'sublocality');
  const shortAddr = [premise, sublocality, route].filter(Boolean).join(', ');
  const display = shortAddr || result.formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  if (district && city && district.toLowerCase() === city.toLowerCase()) {
    district = _cleanIndianPlace(_pickGoogleComponent(comps, 'administrative_area_level_2'));
  }
  return {
    lat, lng, display,
    address: display,
    city,
    district: district && district.toLowerCase() !== city.toLowerCase() ? district : district,
    state,
    pincode,
    locality: shortAddr,
  };
}

async function _googleGeocode(pathAndQuery) {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?${pathAndQuery}&key=${encodeURIComponent(GOOGLE_KEY)}&region=in&language=en`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status !== 'OK' || !data.results?.length) return null;
  return data.results;
}

async function _pincodeFallback(lat, lng) {
  const results = await _googleGeocode(`latlng=${lat},${lng}&result_type=postal_code`);
  if (!results) return '';
  const best = _pickBestGoogleResult(results, lat, lng);
  return (_pickGoogleComponent(best?.address_components, 'postal_code') || '').replace(/\D/g, '').slice(0, 6);
}

async function _bigDataCloudReverse(lat, lng) {
  try {
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client` +
      `?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const d = await res.json();
    const pincode = (d.postcode || '').replace(/\D/g, '').slice(0, 6);
    const admin = d.localityInfo?.administrative || [];
    const district = admin.find(a => a.order === 4)?.name || admin.find(a => a.order === 5)?.name || '';
    return {
      pincode: /^[1-9]\d{5}$/.test(pincode) ? pincode : '',
      city: _cleanIndianPlace(d.city || d.locality || ''),
      state: d.principalSubdivision || '',
      district: _cleanIndianPlace(district),
    };
  } catch { return null; }
}

function _validPincode(pc) {
  return /^[1-9]\d{5}$/.test(String(pc || '').replace(/\D/g, '').slice(0, 6));
}

function _mergeAddrFields(base, extra) {
  if (!extra) return base;
  const out = { ...base };
  if (!_validPincode(out.pincode) && _validPincode(extra.pincode)) out.pincode = extra.pincode;
  if (!out.city && extra.city) out.city = extra.city;
  if (!out.state && extra.state) out.state = extra.state;
  if (!out.district && extra.district) out.district = extra.district;
  return out;
}

// ── High-accuracy GPS: fresh real-time fix only (reject browser cached positions) ─
const GPS_TARGET_ACCURACY_M = 50;
const GPS_GOOD_ACCURACY_M = 150;
const GPS_TIMEOUT_MS = 30000;
const GPS_MIN_WATCH_MS = 2500;       // wait at least this long for a fresh satellite fix
const GPS_FRESH_MS = 800;            // reading must be newer than watch start

function getAccuratePosition({ onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser.'));
      return;
    }

    const watchStartedAt = Date.now();
    let best = null;
    let bestFresh = null;
    let watchId = null;
    let timeoutId = null;
    let settled = false;

    const isFreshReading = (pos) => {
      const ageMs = Date.now() - (pos.timestamp || 0);
      return ageMs <= GPS_FRESH_MS && pos.timestamp >= watchStartedAt - GPS_FRESH_MS;
    };

    const cleanup = () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };

    const finish = (pos) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(pos);
    };

    const fail = (err) => {
      if (settled) return;
      if (bestFresh) {
        finish(bestFresh);
        return;
      }
      settled = true;
      cleanup();
      reject(err);
    };

    if (signal) {
      signal.addEventListener('abort', () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('Location request cancelled.'));
      }, { once: true });
    }

    timeoutId = setTimeout(() => {
      if (bestFresh) finish(bestFresh);
      else if (best) {
        reject(new Error(
          'Browser returned an old cached location, not your current GPS. ' +
          'Enable GPS on your phone, move outdoors, or search your hospital address.'
        ));
      } else {
        fail(new Error('Location request timed out. Enable GPS on your phone, or search your hospital address.'));
      }
    }, GPS_TIMEOUT_MS);

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (settled || signal?.aborted) return;

        const acc = pos.coords.accuracy ?? Infinity;
        if (!best || acc < best.coords.accuracy) best = pos;

        if (!isFreshReading(pos)) return;

        if (!bestFresh || acc < bestFresh.coords.accuracy) bestFresh = pos;
        onProgress?.(pos);

        const elapsed = Date.now() - watchStartedAt;
        if (acc <= GPS_TARGET_ACCURACY_M && elapsed >= GPS_MIN_WATCH_MS) finish(pos);
        if (acc <= GPS_GOOD_ACCURACY_M && elapsed >= 6000) finish(pos);
      },
      fail,
      { enableHighAccuracy: true, maximumAge: 0, timeout: GPS_TIMEOUT_MS + 5000 }
    );
  });
}

function _accuracyWarningMeters(accuracyM) {
  if (accuracyM == null || !Number.isFinite(accuracyM)) return '';
  const m = Math.round(accuracyM);
  if (m <= GPS_GOOD_ACCURACY_M) {
    return `GPS accuracy ±${m}m — drag the pin to your hospital entrance if needed.`;
  }
  if (m <= 1000) {
    return (
      `Approximate location (±${m}m). Laptops often use Wi‑Fi/internet, not GPS — ` +
      'for Chittoor vs Tirupati accuracy, use a phone with GPS enabled or search your address.'
    );
  }
  return (
    `Very approximate location (±${(m / 1000).toFixed(1)}km) — likely from your internet connection, not GPS. ` +
    'Please search your hospital address or use a mobile phone with location enabled.'
  );
}

async function _nominatimReverse(lat, lng) {
  const url =
    `${NOMINATIM}/reverse?lat=${lat}&lon=${lng}` +
    `&format=json&addressdetails=1&zoom=18&email=${NOM_EMAIL}`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
  if (!res.ok) return null;
  const data = await res.json();
  return _buildAddr(data, lat, lng);
}

async function _googleReverse(lat, lng) {
  const results = await _googleGeocode(`latlng=${lat},${lng}`);
  const best = _pickBestGoogleResult(results, lat, lng);
  if (!best) return null;
  const addr = _buildAddrFromGoogle(best, lat, lng);
  if (!addr.pincode) {
    const pc = await _pincodeFallback(lat, lng);
    if (pc) addr.pincode = pc;
  }
  return addr;
}

// ── Reverse-geocode (lat,lng → address) — merge multiple sources for India ───
async function reverseGeocode(lat, lng) {
  const [googleAddr, nominatimAddr, bdcAddr] = await Promise.all([
    GOOGLE_KEY ? _googleReverse(lat, lng).catch(() => null) : Promise.resolve(null),
    _nominatimReverse(lat, lng).catch(() => null),
    _bigDataCloudReverse(lat, lng).catch(() => null),
  ]);

  let addr = googleAddr || nominatimAddr;
  if (!addr && bdcAddr) {
    addr = {
      lat, lng,
      display: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      city: bdcAddr.city || '',
      district: bdcAddr.district || '',
      state: bdcAddr.state || '',
      pincode: bdcAddr.pincode || '',
      locality: bdcAddr.city || '',
    };
  }
  if (!addr) return null;

  addr = _mergeAddrFields(addr, bdcAddr);
  if (nominatimAddr && !googleAddr) {
    if (!addr.locality && nominatimAddr.locality) addr.locality = nominatimAddr.locality;
    if (!addr.address || addr.address === `${lat.toFixed(6)}, ${lng.toFixed(6)}`) {
      addr.address = nominatimAddr.address || nominatimAddr.display;
      addr.display = nominatimAddr.display || addr.address;
    }
  }
  return addr;
}

async function _placesAutocomplete(query) {
  const url =
    `https://maps.googleapis.com/maps/api/place/autocomplete/json` +
    `?input=${encodeURIComponent(query)}` +
    `&components=country:in` +
    `&types=establishment|geocode` +
    `&key=${encodeURIComponent(GOOGLE_KEY)}`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status !== 'OK' && data.status !== 'ZERO_RESULTS') return [];
  return (data.predictions || []).slice(0, 6);
}

async function _placeDetails(placeId) {
  const url =
    `https://maps.googleapis.com/maps/api/place/details/json` +
    `?place_id=${encodeURIComponent(placeId)}` +
    `&fields=geometry,formatted_address,address_components,name` +
    `&key=${encodeURIComponent(GOOGLE_KEY)}`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status !== 'OK' || !data.result) return null;
  const r = data.result;
  const lat = r.geometry?.location?.lat;
  const lng = r.geometry?.location?.lng;
  if (lat == null || lng == null) return null;
  return {
    display_name: r.formatted_address || r.name || '',
    lat,
    lon: lng,
    _google: { address_components: r.address_components, formatted_address: r.formatted_address, geometry: r.geometry },
  };
}

// ── Search (query → Nominatim-shaped results[]) ───────────────────────────────
async function searchPlaces(query) {
  if (GOOGLE_KEY) {
    try {
      const preds = await _placesAutocomplete(query);
      if (preds.length) {
        const detailed = await Promise.all(
          preds.map(p => _placeDetails(p.place_id).catch(() => null))
        );
        return detailed.filter(Boolean);
      }
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&components=country:IN&key=${encodeURIComponent(GOOGLE_KEY)}&region=in&language=en`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'OK' && Array.isArray(data.results)) {
        return data.results.slice(0, 5).map(r => ({
          display_name: r.formatted_address,
          lat: r.geometry?.location?.lat,
          lon: r.geometry?.location?.lng,
          _google: r,
        }));
      }
    } catch (_) { /* fall through */ }
  }
  try {
    const url =
      `${NOMINATIM}/search?q=${encodeURIComponent(query)}` +
      `&format=json&countrycodes=in&limit=5&addressdetails=1&email=${NOM_EMAIL}`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
    if (!res.ok) return [];
    return await res.json();
  } catch { return []; }
}

// ── Extract Indian address fields from a Nominatim response ───────────────────
function _stripPlaceSuffix(s) {
  return String(s || '')
    .replace(/\s*\(Urban\)\s*/gi, ' ')
    .replace(/\s+Rural\s*$/i, '')
    .replace(/ (District|Mandal|Taluk|Tehsil|Division)$/i, '')
    .trim();
}

function _parseDisplayNameIndia(display) {
  const parts = String(display || '').split(',').map((p) => p.trim()).filter(Boolean);
  let state = '';
  let city = '';
  let district = '';
  for (const p of parts) {
    if (!state && /pradesh|nadu|kerala|karnataka|telangana|bihar|goa|delhi|ladakh|jammu|kashmir|islands|territory/i.test(p)) {
      state = p;
    }
  }
  const placeParts = parts.filter((p) => p !== state && !/^\d{6}$/.test(p) && !/^india$/i.test(p));
  if (placeParts.length >= 2) {
    city = placeParts[placeParts.length - 2] || placeParts[0];
    district = placeParts[placeParts.length - 3] || placeParts[1] || city;
  } else if (placeParts.length === 1) {
    city = placeParts[0];
    district = placeParts[0];
  }
  return {
    state: _stripPlaceSuffix(state),
    city: _stripPlaceSuffix(city),
    district: _stripPlaceSuffix(district),
  };
}

function _buildAddr(data, lat, lng) {
  const a        = data?.address || {};
  const display  = data?.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  let rawCity    = a.city || a.town || a.municipality || a.village || a.suburb || a.hamlet || '';
  let rawDist    = a.county || a.state_district || a.district || '';
  let state      = a.state || '';
  const pincode  = (a.postcode || '').replace(/\D/g, '').slice(0, 6);

  const city     = _stripPlaceSuffix(rawCity);
  let district   = _stripPlaceSuffix(rawDist);

  if (!state || !city) {
    const parsed = _parseDisplayNameIndia(display);
    state = state || parsed.state;
    if (!rawCity) rawCity = parsed.city;
    if (!rawDist) rawDist = parsed.district;
  }

  const cityFinal = _stripPlaceSuffix(rawCity || city);
  district = _stripPlaceSuffix(rawDist || district);
  if (!district && cityFinal) district = cityFinal;

  const route = [a.road, a.neighbourhood, a.quarter, a.residential].filter(Boolean).join(', ');
  const locality = route || cityFinal || display.split(',')[0] || '';

  return {
    lat, lng, display,
    address:  display,
    city:     cityFinal,
    district,
    state,
    pincode,
    locality,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function LocationMapPicker({
  value, onChange, onSave, saving = false, height = 320, compact = false, readOnly = false,
}) {
  const containerRef   = useRef(null);
  const mapRef         = useRef(null);   // Leaflet map instance
  const markerRef      = useRef(null);   // Leaflet marker instance
  const accuracyCircleRef = useRef(null);
  const searchTimer    = useRef(null);
  const gpsAbortRef    = useRef(null);

  const [ready,           setReady]           = useState(false);
  const [loadError,       setLoadError]       = useState('');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchResults,   setSearchResults]   = useState([]);
  const [searching,       setSearching]       = useState(false);
  const [showDropdown,    setShowDropdown]    = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [geocoding,       setGeocoding]       = useState(false);
  const [warning,         setWarning]         = useState('');

  const hasLocation = !!(value && value.lat != null && value.lng != null);

  const updateAccuracyCircle = useCallback((lat, lng, accuracyM) => {
    const map = mapRef.current;
    if (!map || !window.L) return;
    const L = window.L;
    if (accuracyCircleRef.current) {
      map.removeLayer(accuracyCircleRef.current);
      accuracyCircleRef.current = null;
    }
    if (accuracyM != null && Number.isFinite(accuracyM) && accuracyM > 0) {
      const color = accuracyM <= GPS_GOOD_ACCURACY_M ? '#22c55e' : accuracyM <= 1000 ? '#f59e0b' : '#ef4444';
      accuracyCircleRef.current = L.circle([lat, lng], {
        radius: accuracyM,
        color,
        fillColor: color,
        fillOpacity: 0.12,
        weight: 1.5,
      }).addTo(map);
    }
  }, []);

  const clearAccuracyCircle = useCallback(() => {
    if (accuracyCircleRef.current && mapRef.current) {
      mapRef.current.removeLayer(accuracyCircleRef.current);
      accuracyCircleRef.current = null;
    }
    setLocationAccuracy(null);
  }, []);

  // ── Core: set location from raw coords → reverse-geocode → call onChange ────
  const setLocationFromCoords = useCallback(async (lat, lng, { clearWarning = true, replacePrevious = false } = {}) => {
    if (clearWarning) setWarning('');
    // Optimistic update — clear old address fields when replacing with live GPS
    onChange?.({
      lat, lng,
      display: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      address: '',
      city: '',
      district: '',
      state: '',
      pincode: '',
      locality: '',
      _liveGps: replacePrevious,
    });
    setGeocoding(true);
    const addr = await reverseGeocode(lat, lng);
    setGeocoding(false);
    const issues = [];
    if (addr) {
      onChange?.(addr);
      if (!addr.city || !addr.state) issues.push('city/state');
      if (!addr.pincode || addr.pincode.length !== 6) issues.push('pincode');
      if (!addr.district) issues.push('district');
      if (issues.length && clearWarning) {
        setWarning(
          `Location pinned. Please verify ${issues.join(', ')} below — ` +
          'drag the pin to your hospital entrance for best accuracy.'
        );
      }
    } else {
      onChange?.({
        lat, lng,
        display: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        address: '', city: '', district: '', state: '', pincode: '',
      });
      if (clearWarning) {
        setWarning(
          'Could not auto-fetch address details. Coordinates are saved — ' +
          'please type city and state manually.'
        );
      }
    }
    return { addr, issues };
  }, [onChange]);

  // ── Initialize Leaflet on mount ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current || mapRef.current) return;

        const initLat  = value?.lat ?? INDIA.lat;
        const initLng  = value?.lng ?? INDIA.lng;
        const initZoom = hasLocation ? ZOOM_LOCATION : ZOOM_INDIA;

        // Create map
        const map = L.map(containerRef.current, {
          center:              [initLat, initLng],
          zoom:                initZoom,
          zoomControl:         true,
          attributionControl:  true,
          scrollWheelZoom:     !readOnly,
          dragging:            !readOnly,
          touchZoom:           !readOnly,
          doubleClickZoom:     !readOnly,
          boxZoom:             !readOnly,
          keyboard:            !readOnly,
        });

        // CartoDB Positron tiles — clean white/light map
        L.tileLayer(
          'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
          {
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>' +
              ' &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>',
            subdomains: 'abcd',
            maxZoom:    20,
          }
        ).addTo(map);

        // Hospital marker
        const icon   = makeHospitalIcon(L);
        const marker = L.marker(
          hasLocation ? [value.lat, value.lng] : [initLat, initLng],
          { draggable: !readOnly, icon }
        );
        if (hasLocation) marker.addTo(map);

        if (!readOnly) {
          // Drag pin → reverse-geocode
          marker.on('dragend', () => {
            const pos = marker.getLatLng();
            setLocationFromCoords(pos.lat, pos.lng);
          });

          // Click map → move pin + reverse-geocode
          map.on('click', (e) => {
            const { lat, lng } = e.latlng;
            marker.setLatLng([lat, lng]);
            if (!map.hasLayer(marker)) marker.addTo(map);
            setLocationFromCoords(lat, lng);
          });
        }

        mapRef.current    = map;
        markerRef.current = marker;
        setReady(true);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Failed to load map');
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_) {}
        mapRef.current    = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync external value changes → marker position ─────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current || !markerRef.current) return;
    if (hasLocation) {
      const pos = [value.lat, value.lng];
      markerRef.current.setLatLng(pos);
      if (!mapRef.current.hasLayer(markerRef.current)) {
        markerRef.current.addTo(mapRef.current);
      }
      const zoom = mapRef.current.getZoom() || 0;
      mapRef.current.panTo(pos);
      if (zoom < 14) mapRef.current.setZoom(ZOOM_LOCATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng, ready]);

  // ── Search bar: debounced Nominatim lookup ────────────────────────────────
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setShowDropdown(false);
    clearTimeout(searchTimer.current);

    if (!q.trim() || q.length < 3) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      const results = await searchPlaces(q);
      setSearchResults(results);
      setSearching(false);
      setShowDropdown(results.length > 0);
    }, 450);
  };

  const handleSelectResult = (result) => {
    const lat  = parseFloat(result.lat);
    const lng  = parseFloat(result.lon);
    const addr = result._google
      ? _buildAddrFromGoogle(result._google, lat, lng)
      : _buildAddr(result, lat, lng);
    setSearchQuery(result.display_name || addr.display);
    setShowDropdown(false);
    setSearchResults([]);

    if (mapRef.current && markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
      if (!mapRef.current.hasLayer(markerRef.current)) {
        markerRef.current.addTo(mapRef.current);
      }
      mapRef.current.setView([lat, lng], ZOOM_LOCATION);
    }
    onChange?.(addr);
    setWarning('');
  };

  // ── "Use my location" — fresh real-time GPS only, not saved/cached location ──
  const useCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    if (gpsAbortRef.current) gpsAbortRef.current.abort();
    const abort = new AbortController();
    gpsAbortRef.current = abort;

    setGettingLocation(true);
    setWarning('');
    clearAccuracyCircle();

    // Clear old saved address/city immediately — do not show previous location text while fetching live GPS
    onChange?.({
      lat: value?.lat ?? null,
      lng: value?.lng ?? null,
      address: '',
      city: '',
      district: '',
      state: '',
      pincode: '',
      locality: '',
      _liveGps: true,
    });

    const applyLiveCoords = (lat, lng, accuracy, notifyParent = false) => {
      setLocationAccuracy(accuracy ?? null);
      updateAccuracyCircle(lat, lng, accuracy);
      if (mapRef.current && markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
        if (!mapRef.current.hasLayer(markerRef.current)) {
          markerRef.current.addTo(mapRef.current);
        }
        mapRef.current.panTo([lat, lng]);
      }
      if (notifyParent) {
        onChange?.({
          lat, lng,
          display: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          address: '', city: '', district: '', state: '', pincode: '', locality: '',
          _liveGps: true,
        });
      }
    };

    try {
      const position = await getAccuratePosition({
        signal: abort.signal,
        onProgress: ({ coords: { latitude: lat, longitude: lng, accuracy } }) => {
          applyLiveCoords(lat, lng, accuracy, true);
        },
      });

      const { latitude: lat, longitude: lng, accuracy } = position.coords;
      applyLiveCoords(lat, lng, accuracy);

      if (mapRef.current) {
        const zoom = accuracy != null && accuracy <= GPS_GOOD_ACCURACY_M ? 18 : 17;
        mapRef.current.setView([lat, lng], zoom);
      }

      const { issues } = await setLocationFromCoords(lat, lng, {
        clearWarning: false,
        replacePrevious: true,
      });

      const parts = [];
      const accMsg = _accuracyWarningMeters(accuracy);
      if (accMsg) parts.push(accMsg);
      if (issues.length) {
        parts.push(
          `Please verify ${issues.join(', ')} below — drag the pin to your hospital entrance.`
        );
      }
      if (parts.length) setWarning(parts.join(' '));
    } catch (err) {
      if (abort.signal.aborted) return;
      const code = err?.code;
      let msg = err?.message || 'Could not get your location.';
      if (code === 1) {
        msg = 'Location permission denied. Allow location access in your browser settings, then try again.';
      } else if (code === 2) {
        msg = 'Location unavailable. Enable GPS on your phone or search your hospital address.';
      } else if (code === 3) {
        msg = 'Location timed out. Move outdoors with GPS enabled, or search your hospital address.';
      }
      alert(msg);
    } finally {
      if (gpsAbortRef.current === abort) gpsAbortRef.current = null;
      setGettingLocation(false);
    }
  }, [setLocationFromCoords, updateAccuracyCircle, clearAccuracyCircle, onChange, value?.lat, value?.lng]);

  // ── Clear ──────────────────────────────────────────────────────────────────
  const clearLocation = () => {
    setWarning('');
    setSearchQuery('');
    clearAccuracyCircle();
    onChange?.(null);
    if (markerRef.current && mapRef.current) {
      if (mapRef.current.hasLayer(markerRef.current)) {
        mapRef.current.removeLayer(markerRef.current);
      }
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* ── Search + Use my location row ──────────────────────────────────── */}
      {!readOnly && <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: 8 }}>

          {/* Search input */}
          <div style={{ position: 'relative', flex: 1 }}>
            <MdSearch style={{
              position: 'absolute', left: 11, top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)', fontSize: 17, zIndex: 1,
              pointerEvents: 'none',
            }} />
            <input
              className="form-input"
              style={{ paddingLeft: 36, width: '100%', boxSizing: 'border-box' }}
              placeholder="Search hospital address (e.g. Apollo Hospitals, Jubilee Hills)"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              autoComplete="off"
              spellCheck={false}
            />

            {/* Search results dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                zIndex: 9999, background: '#1a2035',
                border: '1px solid var(--surface-strong)',
                borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
                maxHeight: 230, overflowY: 'auto',
              }}>
                {searchResults.map((r, i) => (
                  <div
                    key={i}
                    onMouseDown={() => handleSelectResult(r)}
                    style={{
                      padding: '9px 12px', cursor: 'pointer', fontSize: 12.5,
                      color: 'var(--text-primary)', lineHeight: 1.4,
                      borderBottom: i < searchResults.length - 1
                        ? '1px solid var(--surface-muted)' : 'none',
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(96,165,250,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <MdLocationOn style={{ color: '#ef4444', fontSize: 15, marginTop: 2, flexShrink: 0 }} />
                    <span>{r.display_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Use my location button */}
          <button
            type="button"
            onClick={useCurrentLocation}
            disabled={gettingLocation || !ready}
            title="Use my current location"
            style={{
              padding: '0 14px',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              border: 'none', borderRadius: 'var(--radius-md, 8px)',
              color: '#fff', fontWeight: 600,
              cursor: (gettingLocation || !ready) ? 'wait' : 'pointer',
              fontSize: 13, whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: (gettingLocation || !ready) ? 0.65 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <MdMyLocation style={{ fontSize: 16 }} />
            {gettingLocation ? 'Getting GPS…' : 'Use my location'}
          </button>
        </div>
      </div>}

      {/* ── Tip ─────────────────────────────────────────────────────────────── */}
      {!readOnly && <div style={{
        fontSize: 11.5, color: 'rgba(96,165,250,0.85)',
        display: 'flex', alignItems: 'center', gap: 6, padding: '0 2px',
      }}>
        <span>💡</span>
        <span>
          <strong>Tip:</strong> Search, then <strong>drag the pin to your hospital entrance</strong>. Click
          anywhere on the map. Drag the red pin to fine-tune.
        </span>
      </div>}

      {readOnly && (
        <div style={{
          fontSize: 12, color: 'var(--text-muted)',
          background: 'rgba(96,165,250,0.08)',
          border: '1px solid rgba(96,165,250,0.2)',
          borderRadius: 8, padding: '8px 12px', lineHeight: 1.45,
        }}>
          Location is locked after registration. Contact HCEN support if you need to update your hospital pin.
        </div>
      )}

      {/* ── Status banners ───────────────────────────────────────────────────── */}
      {loadError && (
        <div style={{
          fontSize: 12.5, color: '#ef4444',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8, padding: '10px 12px', lineHeight: 1.5,
        }}>
          ⚠️ <strong>Map error:</strong> {loadError}
        </div>
      )}
      {searching && (
        <div style={{
          fontSize: 12, color: '#60a5fa',
          background: 'rgba(96,165,250,0.08)',
          border: '1px solid rgba(96,165,250,0.25)',
          borderRadius: 8, padding: '7px 11px',
        }}>
          🔍 Searching…
        </div>
      )}
      {gettingLocation && !loadError && (
        <div style={{
          fontSize: 12, color: '#60a5fa',
          background: 'rgba(96,165,250,0.08)',
          border: '1px solid rgba(96,165,250,0.25)',
          borderRadius: 8, padding: '7px 11px', lineHeight: 1.45,
        }}>
          📡 Getting your <strong>live GPS</strong> location (not your saved address)… Keep your phone steady outdoors (up to 30s).
        </div>
      )}
      {geocoding && !loadError && (
        <div style={{
          fontSize: 12, color: '#60a5fa',
          background: 'rgba(96,165,250,0.08)',
          border: '1px solid rgba(96,165,250,0.25)',
          borderRadius: 8, padding: '7px 11px',
        }}>
          🔍 Looking up address from coordinates…
        </div>
      )}
      {warning && !geocoding && !loadError && (
        <div style={{
          fontSize: 12, color: '#fbbf24',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 8, padding: '8px 11px', lineHeight: 1.45,
        }}>
          ⚠️ {warning}
        </div>
      )}

      {/* ── Map container ────────────────────────────────────────────────────── */}
      <div
        ref={containerRef}
        style={{
          height, width: '100%',
          borderRadius: 12,
          border: '1px solid var(--border-color-hover)',
          overflow: 'hidden',
          background: '#e8eaed',
          position: 'relative',
        }}
      >
        {!ready && !loadError && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-muted)', fontSize: 13, gap: 8,
          }}>
            🌍 Loading map…
          </div>
        )}
      </div>

      {/* ── Confirmation card (when location is set) ─────────────────────────── */}
      {!compact && hasLocation && (
        <div style={{
          background: 'rgba(16,185,129,0.08)',
          border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 12, padding: '12px 14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <MdCheckCircle style={{ color: '#34d399', fontSize: 18 }} />
            <span style={{ fontWeight: 700, color: '#34d399', fontSize: 13 }}>
              {readOnly ? 'Registered Location' : 'Location Confirmed'}
            </span>
            {!readOnly && (
              <button
                type="button" onClick={clearLocation}
                style={{
                  marginLeft: 'auto',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 6, padding: '3px 10px',
                  cursor: 'pointer', color: '#ef4444',
                  fontSize: 11, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <MdEdit style={{ fontSize: 12 }} /> Clear
              </button>
            )}
          </div>

          {value.address && (
            <div style={{
              fontSize: 12, color: 'var(--text-secondary)',
              marginBottom: 8, lineHeight: 1.5,
            }}>
              {value.address}
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: 6,
          }}>
            {[
              ['City',     value.city     || '—'],
              ['District', value.district || '—'],
              ['State',    value.state    || '—'],
              ['Pincode',  value.pincode  || '—'],
              ['Lat',      value.lat?.toFixed(6)],
              ['Lng',      value.lng?.toFixed(6)],
              ...(locationAccuracy != null ? [['GPS ±m', Math.round(locationAccuracy)]] : []),
            ].map(([label, val]) => (
              <div key={label} style={{
                background: 'rgba(0,0,0,0.25)', borderRadius: 6, padding: '5px 9px',
              }}>
                <div style={{
                  fontSize: 9.5, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>{label}</div>
                <div style={{
                  fontSize: 11.5, color: '#f1f5f9', fontWeight: 600, marginTop: 2,
                }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'flex', flexWrap: 'wrap',
            alignItems: 'center', gap: 10, marginTop: 10,
          }}>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${value.lat},${value.lng}`}
              target="_blank" rel="noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11.5, color: '#60a5fa',
                textDecoration: 'none', fontWeight: 600,
              }}
            >
              🗺️ Open in Google Maps →
            </a>

            {onSave && (
              <button
                type="button" onClick={() => onSave(value)} disabled={saving}
                style={{
                  marginLeft: 'auto', padding: '7px 16px',
                  background: saving
                    ? 'rgba(0,212,170,0.15)'
                    : 'linear-gradient(135deg,#00d4aa,#008080)',
                  border: 'none', borderRadius: 8,
                  color: saving ? '#5eead4' : '#000',
                  fontWeight: 700, fontSize: 12,
                  cursor: saving ? 'wait' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {saving
                  ? 'Saving…'
                  : <><MdCheckCircle style={{ fontSize: 14 }} /> Save Location</>
                }
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── No location prompt ───────────────────────────────────────────────── */}
      {!hasLocation && !compact && (
        <div style={{
          fontSize: 12, color: 'rgba(245,158,11,0.85)',
          background: 'rgba(245,158,11,0.07)',
          border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 8, padding: '8px 12px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <MdLocationOn style={{ fontSize: 16, flexShrink: 0 }} />
          <span>
            {readOnly
              ? 'No location on file yet. Contact HCEN support to set your hospital pin.'
              : 'No location set yet — search, click the map, or use your current location.'}
          </span>
        </div>
      )}
    </div>
  );
}
